import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosTeams from '../../config/axiosInstanceTeams';

// Initial state (same pattern as Slack)
const initialState = {
  teamstoken: localStorage.getItem('teamstoken'),
  teamsisAuthenticated: false,
  teamsloading: !!localStorage.getItem('teamstoken'), // Only loading if we have a token to verify
  teamsuser: null,
  teamserror: null
};

// Async thunk for Teams login (similar to Slack login)
export const teamsLogin = createAsyncThunk(
  'teamsAuth/login',
  async (authData, { rejectWithValue }) => {
    try {
      const config = {
        headers: { 
          'Content-Type': 'application/json',
        }
      };

      const response = await axiosTeams.post('/api/teams/billingauth', authData, config);
      
      // Store token in localStorage (same as Slack)
      localStorage.setItem('teamstoken', response.data.token);
      
      return response.data;
    } catch (error) {
      console.error('Teams login error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for loading Teams user (similar to Slack loadUser)
export const loadTeamsUser = createAsyncThunk(
  'teamsAuth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('teamstoken');
      
      if (!token) {
        throw new Error('No token found');
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const response = await axiosTeams.get('/api/teams/billingauth/verify', config);
      return response.data;
    } catch (error) {
      console.error('Load Teams user error');
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Teams auth slice
const teamsAuthSlice = createSlice({
  name: 'teamsAuth',
  initialState,
  reducers: {
    // Teams logout (same as Slack logout)
    teamsLogout: (state) => {
      localStorage.removeItem('teamstoken');
      state.teamstoken = null;
      state.teamsisAuthenticated = false;
      state.teamsloading = false;
      state.teamsuser = null;
      state.teamserror = null;
    },
    // Clear Teams error
    clearTeamsError: (state) => {
      state.teamserror = null;
    },
    // Set Teams authentication from existing token (for app initialization)
    setTeamsAuthFromToken: (state) => {
      const token = localStorage.getItem('teamstoken');
      if (token) {
        try {
          // Simple check if token is expired (without verification)
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Date.now() / 1000;
          
          if (payload.exp && payload.exp > now) {
            // Token is still valid
            state.teamstoken = token;
            state.teamsisAuthenticated = true;
            state.teamsloading = false;
          } else {
            // Token is expired, remove it
            localStorage.removeItem('teamstoken');
            state.teamstoken = null;
            state.teamsisAuthenticated = false;
            state.teamsloading = false;
          }
        } catch (error) {
          // Invalid token format, remove it
          localStorage.removeItem('teamstoken');
          state.teamstoken = null;
          state.teamsisAuthenticated = false;
          state.teamsloading = false;
        }
      } else {
        state.teamsloading = false;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Teams login
      .addCase(teamsLogin.pending, (state) => {
        state.teamsloading = true;
        state.teamserror = null;
      })
      .addCase(teamsLogin.fulfilled, (state, action) => {
        state.teamstoken = action.payload.token;
        state.teamsisAuthenticated = true;
        state.teamsloading = false;
        state.teamserror = null;
      })
      .addCase(teamsLogin.rejected, (state, action) => {
        state.teamstoken = null;
        state.teamsisAuthenticated = false;
        state.teamsloading = false;
        state.teamserror = action.payload;
        localStorage.removeItem('teamstoken');
      })
      // Load Teams user
      .addCase(loadTeamsUser.pending, (state) => {
        state.teamsloading = true;
      })
      .addCase(loadTeamsUser.fulfilled, (state, action) => {
        state.teamsuser = action.payload;
        state.teamsisAuthenticated = true;
        state.teamsloading = false;
        state.teamserror = null;
      })
      .addCase(loadTeamsUser.rejected, (state, action) => {
        state.teamsuser = null;
        state.teamsisAuthenticated = false;
        state.teamsloading = false;
        state.teamserror = null; // Don't show error for expired tokens
        
        // Always clear token when loadTeamsUser fails (expired/invalid)
        localStorage.removeItem('teamstoken');
        state.teamstoken = null;
      });
  }
});

export const { teamsLogout, clearTeamsError, setTeamsAuthFromToken } = teamsAuthSlice.actions;
export default teamsAuthSlice.reducer;

